import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../helpers/api';
import * as T from './actionTypes';
import * as A from './actions';

const ROOT = 'materiovigilance';

function buildQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') search.append(k, v);
  });
  return search.toString();
}

function unwrap(response) {
  // Backend wraps payloads as { response, message } via GenericResponse.success
  return response?.data?.response ?? response?.data ?? null;
}

function errorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

// ---------------------------------------------------------------------------
// Cross-flow list
// ---------------------------------------------------------------------------
function* fetchListSaga({ payload }) {
  try {
    const qs = buildQuery(payload);
    const res = yield call(api.get, `${ROOT}/list${qs ? `?${qs}` : ''}`);
    yield put(A.fetchMaterioListSuccess(unwrap(res)));
  } catch (error) {
    yield put(A.fetchMaterioListFail(errorMessage(error, 'Failed to fetch materiovigilance list')));
  }
}

// ---------------------------------------------------------------------------
// DESCENDING
// ---------------------------------------------------------------------------
function* fetchDescSaga({ payload: id }) {
  try {
    const res = yield call(api.get, `${ROOT}/descending/get-by-id?id=${id}`);
    yield put(A.fetchDescSuccess(unwrap(res)));
  } catch (error) {
    yield put(A.fetchDescFail(errorMessage(error, 'Failed to fetch record')));
  }
}

function* createDescSaga({ payload: { data, callback } }) {
  try {
    const res = yield call(api.post, `${ROOT}/descending/create`, data);
    const item = unwrap(res);
    yield put(A.createDescSuccess(item));
    if (callback) callback(item);
  } catch (error) {
    yield put(A.createDescFail(errorMessage(error, 'Failed to create record')));
  }
}

function* updateDescSaga({ payload: { data, callback } }) {
  try {
    const res = yield call(api.put, `${ROOT}/descending/update`, data);
    const item = unwrap(res);
    yield put(A.updateDescSuccess(item));
    if (callback) callback(item);
  } catch (error) {
    yield put(A.updateDescFail(errorMessage(error, 'Failed to update record')));
  }
}

function* deleteDescSaga({ payload: { id, callback } }) {
  try {
    yield call(api.delete, `${ROOT}/descending?id=${id}`);
    yield put(A.deleteDescSuccess(id));
    if (callback) callback();
  } catch (error) {
    yield put(A.deleteDescFail(errorMessage(error, 'Failed to delete record')));
  }
}

function* changeStatusDescSaga({ payload: { data, callback } }) {
  try {
    const res = yield call(api.put, `${ROOT}/descending/change-status`, data);
    const item = unwrap(res);
    yield put(A.changeStatusDescSuccess(item));
    if (callback) callback(item);
  } catch (error) {
    yield put(A.changeStatusDescFail(errorMessage(error, 'Failed to change status')));
  }
}

function* addDescAttachmentSaga({ payload: { data, callback } }) {
  try {
    const res = yield call(api.post, `${ROOT}/descending/attachments/add`, data);
    const att = unwrap(res);
    yield put(A.addDescAttachmentSuccess(att));
    if (callback) callback(att);
  } catch (error) {
    yield put(A.addDescAttachmentFail(errorMessage(error, 'Failed to add attachment')));
  }
}

function* removeDescAttachmentSaga({ payload: { attachmentId, callback } }) {
  try {
    yield call(api.delete, `${ROOT}/descending/attachments/remove?attachmentId=${attachmentId}`);
    yield put(A.removeDescAttachmentSuccess(attachmentId));
    if (callback) callback();
  } catch (error) {
    yield put(A.removeDescAttachmentFail(errorMessage(error, 'Failed to remove attachment')));
  }
}

// ---------------------------------------------------------------------------
// ASCENDING
// ---------------------------------------------------------------------------
function* fetchAscSaga({ payload: id }) {
  try {
    const res = yield call(api.get, `${ROOT}/ascending/get-by-id?id=${id}`);
    yield put(A.fetchAscSuccess(unwrap(res)));
  } catch (error) {
    yield put(A.fetchAscFail(errorMessage(error, 'Failed to fetch record')));
  }
}

function* createAscSaga({ payload: { data, callback } }) {
  try {
    const res = yield call(api.post, `${ROOT}/ascending/create`, data);
    const item = unwrap(res);
    yield put(A.createAscSuccess(item));
    if (callback) callback(item);
  } catch (error) {
    yield put(A.createAscFail(errorMessage(error, 'Failed to create record')));
  }
}

function* updateAscSaga({ payload: { data, callback } }) {
  try {
    const res = yield call(api.put, `${ROOT}/ascending/update`, data);
    const item = unwrap(res);
    yield put(A.updateAscSuccess(item));
    if (callback) callback(item);
  } catch (error) {
    yield put(A.updateAscFail(errorMessage(error, 'Failed to update record')));
  }
}

function* deleteAscSaga({ payload: { id, callback } }) {
  try {
    yield call(api.delete, `${ROOT}/ascending?id=${id}`);
    yield put(A.deleteAscSuccess(id));
    if (callback) callback();
  } catch (error) {
    yield put(A.deleteAscFail(errorMessage(error, 'Failed to delete record')));
  }
}

function* changeStatusAscSaga({ payload: { data, callback } }) {
  try {
    const res = yield call(api.put, `${ROOT}/ascending/change-status`, data);
    const item = unwrap(res);
    yield put(A.changeStatusAscSuccess(item));
    if (callback) callback(item);
  } catch (error) {
    yield put(A.changeStatusAscFail(errorMessage(error, 'Failed to change status')));
  }
}

// ---------------------------------------------------------------------------
// Activity (shared)
// ---------------------------------------------------------------------------
function* fetchActivitySaga({ payload: { flowKind, id } }) {
  try {
    const res = yield call(api.get, `${ROOT}/activity?flowKind=${flowKind}&id=${id}`);
    yield put(A.fetchMaterioActivitySuccess(unwrap(res)));
  } catch (error) {
    yield put(A.fetchMaterioActivityFail(errorMessage(error, 'Failed to fetch activity')));
  }
}

export default function* materiovigilanceSaga() {
  yield takeLatest(T.FETCH_MATERIO_LIST, fetchListSaga);

  yield takeLatest(T.FETCH_DESC, fetchDescSaga);
  yield takeLatest(T.CREATE_DESC, createDescSaga);
  yield takeLatest(T.UPDATE_DESC, updateDescSaga);
  yield takeLatest(T.DELETE_DESC, deleteDescSaga);
  yield takeLatest(T.CHANGE_STATUS_DESC, changeStatusDescSaga);
  yield takeLatest(T.ADD_DESC_ATTACHMENT, addDescAttachmentSaga);
  yield takeLatest(T.REMOVE_DESC_ATTACHMENT, removeDescAttachmentSaga);

  yield takeLatest(T.FETCH_ASC, fetchAscSaga);
  yield takeLatest(T.CREATE_ASC, createAscSaga);
  yield takeLatest(T.UPDATE_ASC, updateAscSaga);
  yield takeLatest(T.DELETE_ASC, deleteAscSaga);
  yield takeLatest(T.CHANGE_STATUS_ASC, changeStatusAscSaga);

  yield takeLatest(T.FETCH_MATERIO_ACTIVITY, fetchActivitySaga);
}
